
import React, { useState, useEffect } from 'react';
import { X, Save, ShieldCheck, Tag } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Program } from '../../types';

interface ProgramFormModalProps {
  program?: Program | null;
  onClose: () => void;
}

export const ProgramFormModal: React.FC<ProgramFormModalProps> = ({ program, onClose }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    category_id: ''
  });

  // Graceful fetch handling for lookups
  const { data: categories } = useQuery({ 
    queryKey: ['lookup', 'program_categories'], 
    queryFn: async () => {
      const { data, error } = await supabase.from('program_categories').select('*');
      if (error) return [];
      return data;
    },
    initialData: []
  });

  useEffect(() => {
    if (program) {
      setFormData({
        name: program.name,
        description: program.description || '',
        is_active: program.is_active,
        category_id: program.category_id || ''
      });
    }
  }, [program]);

  const upsertProgramMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = { 
        name: data.name, 
        description: data.description, 
        is_active: data.is_active,
        category_id: data.category_id || null
      };
      if (program) {
        const { error } = await supabase.from('programs').update(payload).eq('id', program.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('programs').insert([{ ...payload, created_by_email: profile?.email }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['program', program?.id] });
      onClose();
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-xl rounded-lg shadow-2xl border border-slate-100 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="p-8 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight flex items-center gap-3">
            <ShieldCheck size={24} className="text-[#FFDB00]" />
            {program ? 'Edit Program' : 'New Program'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={24} />
          </button>
        </div>
        <form 
          className="p-8 space-y-6 max-h-[70vh] overflow-y-auto"
          onSubmit={(e) => {
            e.preventDefault();
            upsertProgramMutation.mutate(formData);
          }}
        >
          <Input 
            label="Name" 
            required 
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
          />
          
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Category</label>
            <select 
              className="w-full px-5 py-3 bg-white border border-slate-200 rounded-lg font-bold outline-none focus:ring-4 focus:ring-slate-100"
              value={formData.category_id}
              onChange={e => setFormData({ ...formData, category_id: e.target.value })}
            >
              <option value="">Select Category...</option>
              {categories?.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <Input 
            label="Description" 
            isTextArea 
            rows={3} 
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <p className="text-[10px] font-black uppercase text-slate-900 tracking-widest">Active Status</p>
              <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">Include in global impact reports</p>
            </div>
            <input 
              type="checkbox" 
              checked={formData.is_active}
              onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-6 h-6 rounded accent-slate-900" 
            />
          </div>

          <Button 
            type="submit" 
            className="w-full py-4" 
            isLoading={upsertProgramMutation.isPending}
            icon={<Save size={18} />}
          >
            {program ? 'Save Changes' : 'Create Program'}
          </Button>
        </form>
      </div>
    </div>
  );
};
